// ЄДИНЕ джерело правди для навігації SCADA.
//
// Раніше підменю існувало у двох місцях: SUBNAV у ScadaLayout (7 пунктів,
// з підписами) і власний navItems у картці станції (10 пунктів, icon-only).
// Через це ряд змінював склад і вигляд при переході «вкладка → розділ».
// Тепер обидва списки описані тут, а рендерить їх лише ScadaLayout.
//
// Два роди пунктів:
//   · `tab`     — вкладка картки станції; живе в URL як /scada/station/:id/:tab;
//   · `section` — окремий маршрут SCADA (контракт адрес не змінювався).
import i18n from '@/i18n';
import {
  AlertTriangle, BatteryCharging, BookOpen, Cpu, FileBarChart,
  LayoutDashboard, LayoutGrid, Network, Router, ShieldAlert, SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';

// ─── Вкладки картки станції ──────────────────────────────────────────────────
export const STATION_TABS = ['overview', 'sld', 'inverters', 'bess'] as const;
export type StationTab = (typeof STATION_TABS)[number];
export const DEFAULT_STATION_TAB: StationTab = 'overview';

export const isStationTab = (v?: string | null): v is StationTab =>
  !!v && (STATION_TABS as readonly string[]).includes(v);

export interface StationTabItem {
  key: StationTab;
  label: string;
  Icon: LucideIcon;
  /** Вкладка має сенс лише для СЕС із накопичувачем. */
  needsBess?: boolean;
}

export const STATION_TAB_ITEMS: StationTabItem[] = [
  { key: 'overview', get label() { return i18n.t('ak_nav_overview'); }, Icon: LayoutDashboard },
  { key: 'sld', get label() { return i18n.t('ak_nav_diagram'); }, Icon: Network },
  { key: 'inverters', get label() { return i18n.t('ak_nav_inverters'); }, Icon: Cpu },
  { key: 'bess', get label() { return i18n.t('ak_nav_bess'); }, Icon: BatteryCharging, needsBess: true },
];

// ─── Розділи SCADA (окремі маршрути) ─────────────────────────────────────────
export interface ScadaSection {
  key: string;
  label: string;
  path: string;
  Icon: LucideIcon;
  stage: string;
}

export const SCADA_SECTIONS: ScadaSection[] = [
  { key: 'dispatch', get label() { return i18n.t('ak_nav_equipment_monitoring'); }, path: '/scada/dispatch', Icon: LayoutGrid, stage: 'R1' },
  { key: 'control', get label() { return i18n.t('ak_nav_fleet_control'); }, path: '/scada/control', Icon: SlidersHorizontal, stage: 'R1' },
  { key: 'sko', get label() { return i18n.t('ak_nav_curtailment_sko_dso'); }, path: '/scada/sko', Icon: ShieldAlert, stage: 'R3' },
  { key: 'events', get label() { return i18n.t('ak_nav_events'); }, path: '/scada/events', Icon: AlertTriangle, stage: 'R3' },
  { key: 'shift', get label() { return i18n.t('ak_nav_shift_log'); }, path: '/scada/shift-handover', Icon: BookOpen, stage: 'R3' },
  { key: 'emsbox', get label() { return i18n.t('ak_nav_telemetry'); }, path: '/scada/emsbox', Icon: Router, stage: 'R4' },
  { key: 'reports', get label() { return i18n.t('ak_nav_reports'); }, path: '/scada/reports', Icon: FileBarChart, stage: 'R4' },
];

/**
 * Розділи, що потрапляють у підменю, і в якому порядку.
 *
 * З п'яти кнопок лишилась одна — «Події». «Ручне керування», «СКО / ОСР»,
 * «Журнал зміни» і «Звіти» прибрані з підменю 05.10 на прохання замовника:
 * у кабінеті вони не використовуються, а в ряду поруч із вкладками станції
 * читалися як робочі інструменти.
 *
 * МАРШРУТИ НЕ ВИДАЛЕНІ: /scada/control, /scada/sko, /scada/shift-handover і
 * /scada/reports лишаються зареєстрованими, сторінки відкриваються за адресою
 * і доступні повністю. Прибрана лише точка входу, тож повернути кнопку —
 * дописати її ключ сюди.
 *
 * «Моніторинг обладнання» не входить — його роль виконують вкладки станції
 * зліва; «Телеметрія» має власний пункт у бічному меню.
 */
// 20.10: «Керування парком» повернуто — диспетчери Лучок і Табаків просили
// бачити всі станції з їхніми обмеженнями на одному екрані. Сторінку
// переписано: демо-імітацію виконання команд прибрано, запису уставок
// до введення каналу EMS box немає, і сторінка про це прямо каже.
export const SUBNAV_SECTION_KEYS = ['control', 'events'];

export const sectionByKey = (key: string) => SCADA_SECTIONS.find((s) => s.key === key);
export const sectionByPath = (pathname: string) => SCADA_SECTIONS.find((s) => s.path === pathname);

// ─── Маршрут картки станції ──────────────────────────────────────────────────
export const stationPath = (id: string, tab: StationTab = DEFAULT_STATION_TAB) =>
  `/scada/station/${id}/${tab}`;

/**
 * Розбір адреси картки станції. Приймає і новий вигляд (/:id/:tab), і старий
 * (/:id) — старі посилання лишаються робочими й трактуються як «Огляд».
 */
export function parseStationPath(pathname: string): { id: string; tab: StationTab } | null {
  const m = pathname.match(/^\/scada\/station\/([^/]+)(?:\/([^/]+))?\/?$/);
  if (!m) return null;
  return { id: m[1], tab: isStationTab(m[2]) ? m[2] : DEFAULT_STATION_TAB };
}

/** Ключ активного пункту підменю для поточної адреси. */
export function activeNavKey(pathname: string): string | null {
  const st = parseStationPath(pathname);
  if (st) return `tab:${st.tab}`;
  const sec = sectionByPath(pathname);
  return sec ? `nav:${sec.key}` : null;
}

/** Назва розділу для breadcrumb/топбара. */
export function scadaTitle(pathname: string): string | null {
  if (parseStationPath(pathname)) return sectionByKey('dispatch')!.label;
  return sectionByPath(pathname)?.label ?? null;
}
